import { ASSET_TYPE, IAsset, assetIsDatalake, assetLinkProps, getAssetImage } from '@desp-aas/desp-ui-fwk';
import { sourceMeta } from '../../../utils/icons';
import { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { BsDatabase, BsBraces, BsDisplay, BsFileText, BsMortarboard, BsHeart, BsDownload } from 'react-icons/bs';
import { Tooltip } from 'react-tooltip';
import removeMarkdown from 'remove-markdown';
import './HomeAssetCard.css';

const TYPE_META: Record<string, { icon: React.ReactNode; color: string; label: string }> = {
    [ASSET_TYPE.dataset]:     { icon: <BsDatabase />,    color: 'rgb(239,43,137)',  label: 'Dataset' },
    [ASSET_TYPE.model]:       { icon: <BsBraces />,      color: 'rgb(123,52,219)',  label: 'Model' },
    [ASSET_TYPE.application]: { icon: <BsDisplay />,     color: 'rgb(20,143,150)',  label: 'Application' },
    [ASSET_TYPE.paper]:       { icon: <BsFileText />,    color: 'rgb(247,164,56)',  label: 'Document' },
    [ASSET_TYPE.course]:      { icon: <BsMortarboard />, color: 'rgb(99,193,224)',  label: 'Course' },
};

function hashColor(id: string): number {
    let h = 0;
    for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
    return h % 360;
}

type Props = { asset: IAsset; type?: ASSET_TYPE };

export default function HomeAssetCard({ asset, type }: Props) {
    const [imgError, setImgError] = useState(false);
    const assetType = type ?? asset.type;
    const meta = TYPE_META[assetType] ?? TYPE_META[ASSET_TYPE.dataset];
    const hue = hashColor(asset.id);
    const desc = removeMarkdown(asset.metadata?.description ?? '').trim();
    const shortDesc = desc.length > 95 ? desc.slice(0, 95) + '…' : desc;
    const cssVars = { '--hac-color': meta.color, '--hac-hue': `${hue}deg` } as React.CSSProperties;
    const source = sourceMeta(asset.source);

    const tooltipId = `src-${asset.id}`;

    return (
        <NavLink {...assetLinkProps(asset)} className="hac" style={cssVars}>
            <span
                className={`hac-source-badge hac-source-badge--${source.modifier}`}
                data-tooltip-id={tooltipId}
                data-tooltip-content={source.label}
            >
                {source.icon}
            </span>
            {createPortal(
                <Tooltip id={tooltipId} place="left" positionStrategy="fixed" />,
                document.body
            )}
            <div className="hac-img" data-type={assetType}>
                {!imgError
                    ? <img src={getAssetImage(asset)} alt={asset.name} className="hac-real-img" onError={() => setImgError(true)} />
                    : <span className="hac-img-watermark">{meta.icon}</span>
                }
                <div className="hac-img-overlay" />
            </div>
            <div className="hac-body">
                <div className="hac-type-badge"><span className="hac-type-icon">{meta.icon}</span>{meta.label}</div>
                <div className="hac-name">{asset.name}</div>
                {desc && <div className="hac-desc">{shortDesc}</div>}
                {/* datalake assets have no local likes/downloads to show */}
                {!assetIsDatalake(asset) &&
                    <div className="hac-footer">
                        <span className="hac-stat"><BsHeart /> {asset.likes_count ?? 0}</span>
                        <span className="hac-stat"><BsDownload /> {asset.downloads_count ?? 0}</span>
                    </div>
                }
            </div>
        </NavLink>
    );
}
